import { useEffect, useMemo, useState } from 'react'
import { Download, Search } from 'lucide-react'
import { api, downloadCsv, errorMessage, type AdminUser, type DictItem } from '@/lib/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { fmtRelative } from '@/lib/format'
import { useI18n } from '@/lib/i18n'
import { PageError, PageLoading } from './Stats'

export function UsersScreen() {
  const { t } = useI18n()
  const toast = useToast()
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [adjusting, setAdjusting] = useState<AdminUser | null>(null)
  const [resettingPwd, setResettingPwd] = useState<AdminUser | null>(null)
  const [roleDict, setRoleDict] = useState<DictItem[]>([])

  function load() {
    setBusy(true); setError(null)
    api.listUsers({ q: q || undefined, status: status || undefined, limit: 100 })
      .then((r) => { setUsers(r.users); setTotal(r.total) })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }

  useEffect(() => { load() }, [])
  useEffect(() => {
    api.listDict('user_role')
      .then((r) => setRoleDict(r.items.filter((i) => i.active === 1)))
      .catch(() => {})
  }, [])

  // code → 中文 label, for compact column rendering and toast feedback.
  const roleLabel = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of roleDict) m[r.code] = r.labelZh
    return m
  }, [roleDict])

  async function changeRole(u: AdminUser, role: string) {
    if (role === u.role) return
    try {
      await api.patchUser(u.id, { role })
      setUsers((prev) => prev?.map((x) => (x.id === u.id ? { ...x, role } : x)) ?? null)
      toast.success(u.email, `${roleLabel[u.role] ?? u.role} → ${roleLabel[role] ?? role}`)
    } catch (e) {
      toast.error(t('orders.toast.updateFailed'), errorMessage(e))
    }
  }

  async function toggleStatus(u: AdminUser) {
    const next = u.status === 'active' ? 'disabled' : 'active'
    try {
      await api.patchUser(u.id, { status: next })
      setUsers((prev) => prev?.map((x) => (x.id === u.id ? { ...x, status: next } as AdminUser : x)) ?? null)
      const fromLabel = t(`status.${u.status}` as any)
      const toLabel = t(`status.${next}` as any)
      toast.success(`${u.email}`, t('users.toggle', { from: fromLabel, to: toLabel }))
    } catch (e) {
      toast.error(t('orders.toast.updateFailed'), errorMessage(e))
    }
  }

  async function exportCsv() {
    setExporting(true)
    try {
      await downloadCsv('/admin/users/export.csv', 'users.csv')
      toast.success(t('action.export'), t('orders.toast.exportReady'))
    } catch (e) {
      toast.error(t('orders.toast.exportFailed'), errorMessage(e))
    } finally {
      setExporting(false)
    }
  }

  if (error) return <PageError msg={error}/>
  if (!users && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('users.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('users.subtitle', { n: total.toLocaleString() })}</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={exporting}>
          <Download size={14} strokeWidth={1.75}/>
          {exporting ? t('action.exporting') : t('action.export')}
        </Button>
      </header>

      <form
        className="flex gap-2 items-center"
        onSubmit={(e) => { e.preventDefault(); load() }}
      >
        <div className="relative max-w-sm flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60"/>
          <Input
            placeholder={t('users.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 rounded-md border border-border bg-card px-3 text-sm"
        >
          <option value="">{t('users.filter.all')}</option>
          <option value="active">{t('user.status.active')}</option>
          <option value="disabled">{t('user.status.disabled')}</option>
          <option value="pending">{t('user.status.pending')}</option>
        </select>
        <Button type="submit" disabled={busy}>{t('action.search')}</Button>
      </form>

      <Table>
        <THead>
          <Tr>
            <Th>Email</Th>
            <Th>{t('products.col.name')}</Th>
            <Th>{t('orders.col.status')}</Th>
            <Th>{t('users.col.role')}</Th>
            <Th>Referral</Th>
            <Th className="text-right">{t('users.col.coins')}</Th>
            <Th>{t('orders.col.created')}</Th>
            <Th className="text-right pr-4">{t('products.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {users?.map((u) => (
            <Tr key={u.id}>
              <Td className="font-medium">{u.email}</Td>
              <Td>{u.displayName ?? <span className="text-muted-foreground">—</span>}</Td>
              <Td>
                <Badge tone={u.status === 'active' ? 'success' : u.status === 'disabled' ? 'danger' : 'warning'}>
                  {t(`user.status.${u.status}` as any)}
                </Badge>
              </Td>
              <Td>
                <select
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className="h-7 rounded border border-border bg-card px-2 text-xs"
                >
                  {roleDict.map((r) => (
                    <option key={r.id} value={r.code}>{r.labelZh}</option>
                  ))}
                  {/* Sticky legacy fallback when the user's role isn't in the
                      active dict (deactivated entry, or DB has a code we
                      haven't seeded). Keeps the row editable. */}
                  {u.role && !roleDict.some((r) => r.code === u.role) && (
                    <option value={u.role}>{`(legacy: ${u.role})`}</option>
                  )}
                </select>
              </Td>
              <Td className="font-mono text-xs">{u.referralCode}</Td>
              <Td className="text-right tabular-nums font-mono text-xs">
                <span className={u.walletCents > 0 ? 'text-amber-500 font-semibold' : 'text-muted-foreground'}>
                  {(u.walletCents / 100).toFixed(2)}
                </span>
              </Td>
              <Td className="text-muted-foreground">{fmtRelative(u.createdAt)}</Td>
              <Td className="text-right space-x-1.5 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => setAdjusting(u)}>
                  {t('users.action.adjustWallet')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setResettingPwd(u)}>
                  {t('users.action.setPassword')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleStatus(u)}>
                  {u.status === 'active' ? t('action.disable') : t('action.enable')}
                </Button>
              </Td>
            </Tr>
          ))}
          {users?.length === 0 && (
            <Tr>
              <Td colSpan={8} className="text-center py-8 text-muted-foreground">
                {t('users.empty')}
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>

      {adjusting && (
        <WalletAdjustDialog
          user={adjusting}
          onClose={() => setAdjusting(null)}
          onSaved={(next) => {
            setUsers((prev) => prev?.map((x) => (x.id === next.id ? { ...x, walletCents: next.walletCents } : x)) ?? null)
            setAdjusting(null)
          }}
        />
      )}

      {resettingPwd && (
        <SetPasswordDialog
          user={resettingPwd}
          onClose={() => setResettingPwd(null)}
        />
      )}
    </div>
  )
}

function WalletAdjustDialog({
  user, onClose, onSaved,
}: {
  user: AdminUser
  onClose: () => void
  onSaved: (next: { id: string; walletCents: number }) => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const [delta, setDelta] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const deltaCents = (() => {
    const n = Number(delta)
    if (!Number.isFinite(n)) return 0
    return Math.round(n * 100)
  })()
  const after = Math.max(0, user.walletCents + deltaCents)
  const canSave = !busy && deltaCents !== 0 && note.trim().length > 0

  async function save() {
    setBusy(true)
    try {
      const r = await api.adjustUserWallet(user.id, { deltaCents, note: note.trim() })
      toast.success(user.email, t('users.toast.walletAdjusted'))
      onSaved({ id: user.id, walletCents: r.afterCents })
    } catch (e) {
      toast.error(t('users.toast.walletAdjustFailed'), errorMessage(e))
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2 className="text-lg font-semibold tracking-tight">{t('users.walletDialog.title')}</h2>
          <p className="text-xs text-muted-foreground mt-1">{user.email}</p>
        </header>

        <div className="space-y-3 text-sm">
          <div className="flex justify-between border-b border-border pb-2">
            <span className="text-muted-foreground">{t('users.walletDialog.current')}</span>
            <span className="font-mono tabular-nums font-medium">¥{(user.walletCents / 100).toFixed(2)}</span>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">{t('users.walletDialog.delta')}</label>
            <Input type="number" step={0.01} value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="+10 / -5"/>
            <p className="text-[11px] text-muted-foreground/80 mt-1">{t('users.walletDialog.deltaHint')}</p>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">{t('users.walletDialog.note')}</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={t('users.walletDialog.notePh')}/>
          </div>

          {deltaCents !== 0 && (
            <div className="flex justify-between rounded-md bg-muted/40 px-3 py-2">
              <span className="text-muted-foreground">{t('users.walletDialog.after')}</span>
              <span className="font-mono tabular-nums font-semibold">¥{(after / 100).toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={save} disabled={!canSave}>{t('action.save')}</Button>
        </div>
      </div>
    </div>
  )
}

// 管理员代设密码 —— 服务端无 old-password 校验，admin 即权威。仅校验 8~128
// 位 + 二次输入一致；保存成功不显明文，admin 自行通知用户即可。
function SetPasswordDialog({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const { t } = useI18n()
  const toast = useToast()
  const [pwd, setPwd] = useState('')
  const [pwd2, setPwd2] = useState('')
  const [busy, setBusy] = useState(false)

  const tooShort = pwd.length > 0 && pwd.length < 8
  const mismatch = pwd2.length > 0 && pwd !== pwd2
  const canSave = !busy && pwd.length >= 8 && pwd === pwd2

  async function save() {
    if (!canSave) return
    setBusy(true)
    try {
      await api.setUserPassword(user.id, pwd)
      toast.success(user.email, t('users.toast.passwordSet'))
      onClose()
    } catch (e) {
      toast.error(t('users.toast.passwordSetFailed'), errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg space-y-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">{t('users.passwordDialog.title')}</h2>
          <p className="text-xs text-muted-foreground mt-1 truncate" title={user.email}>{user.email}</p>
        </div>

        <div>
          <label className="text-xs text-muted-foreground">{t('users.passwordDialog.label')}</label>
          <Input type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} />
          <p className="text-[11px] text-muted-foreground/80 mt-1">{t('users.passwordDialog.hint')}</p>
          {tooShort && (
            <p className="text-[11px] text-danger mt-1">{t('users.passwordDialog.tooShort')}</p>
          )}
        </div>

        <div>
          <label className="text-xs text-muted-foreground">{t('users.passwordDialog.confirm')}</label>
          <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} />
          {mismatch && (
            <p className="text-[11px] text-danger mt-1">{t('users.passwordDialog.mismatch')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={save} disabled={!canSave}>{t('action.save')}</Button>
        </div>
      </div>
    </div>
  )
}
